<template>
    <div>
        <div class="dropdown-locations-row mb-26">
            <div
                class="property-dropdown-container"
                :class="isUpsertLocation ? 'has-location-actions' : 'no-location-actions'">
                <div class="property-dropdown dropdown-custom-tooltip">
                    <gofix-form-dropdown
                        class="mb-0"
                        :vLabel="vLabel"
                        :label="label" 
                        :filterable="filterable" 
                        :required="required"
                        :show-icon-required="showIconRequired"
                        :multiple="multiple"
                        :append-to-body="appendToBody"
                        :disabled="disabled"
                        :reduce="reduce"
                        :options="options"
                        @search="searchOptions"
                        :value="value"
                        @input="handleInput" >
                        <template slot="gofixInput:dropdown:selected-option" slot-scope="option">
                            <span class="location-option-label">
                                {{ getOptionLabel(option) }}
                            </span>
                        </template>
                        <template slot="gofixInput:dropdown:option" slot-scope="option">
                            <span
                                class="location-option-label"
                                @mouseenter="showOptionTooltip($event, option)"
                                @mouseleave="hideOptionTooltip"
                                @mousemove="showOptionTooltip($event, option)">
                                {{ getOptionLabel(option) }}
                            </span>
                        </template>
                    </gofix-form-dropdown>
                </div>
                <div v-if="isUpsertLocation" class="location-actions">
                    <a
                        v-if="value"
                        class="control-item-link cursor-pointer location-action-btn"
                        title="Edit location"
                        @click="showUpsertLocation('edit')">
                        <i class="icon-pencil font-lg"></i>
                    </a>
                    <a
                        class="control-item-link cursor-pointer location-action-btn"
                        title="Add location"
                        @click="showUpsertLocation('add')">
                        <i class="icon-plus font-lg"></i>
                    </a>
                </div>
            </div>
        </div>
        <b-modal
            v-if="isInModal"
            :no-close-on-backdrop="true"
            :title="upsertModalTitle"
            hide-footer
            :centered="true"
            size="md"
            header-class="border-0"
            v-model="isShowUpsertLocation">
            <location-upsert-panel
                :location="location"
                :is-create="isCreate"
                :is-save="isSave"
                :is-searching="isSearching"
                @search-postal-code="handleSearchPostalCode"
                @save="saveLocation">
            </location-upsert-panel>
        </b-modal>
        <div v-else-if="isShowUpsertLocation" class="border rounded p-4 mb-26">
            <location-upsert-panel
                :location="location"
                :is-create="isCreate"
                :is-save="isSave"
                :is-searching="isSearching"
                @search-postal-code="handleSearchPostalCode"
                @save="saveLocation">
            </location-upsert-panel>
        </div>
    </div>
</template>

<script>
    const LocationUpsertPanel = {
        name: 'LocationUpsertPanel',
        props: {
            location: {
                type: Object,
                required: true
            },
            isCreate: {
                type: Boolean,
                default: false
            },
            isSave: {
                type: Boolean,
                default: false
            },
            isSearching: {
                type: Boolean,
                default: false
            }
        },
        template: `
            <div>
                <b-row align-v="center">
                    <b-col md="8" v-if="isCreate">
                        <gofix-form-input-number
                            label="Enter 6-digit Postal Code"
                            required
                            show-icon-required
                            :decimalLimit="0"
                            :allowDecimal="false"
                            thousandsSeparatorSymbol=""
                            :integerLimit="6"
                            v-model="location.postal_code">
                        </gofix-form-input-number>
                    </b-col>
                    <b-col md="4" v-if="isCreate">
                        <gofix-button
                            label="Search"
                            :disabled="isSearching || (!location.postal_code || location.postal_code.length < 6)"
                            :loading="isSearching"
                            @click="$emit('search-postal-code')">
                        </gofix-button>
                    </b-col>
                    <b-col md="12">
                        <gofix-form-input
                            label="Location Name"
                            :disabled="isSave || isCreate"
                            v-model="location.name">
                        </gofix-form-input>
                    </b-col>
                    <b-col md="12" v-if="isCreate">
                        <gofix-form-input
                            label="Lift Number"
                            required
                            show-icon-required
                            :disabled="isSave"
                            v-model="location.lift_number">
                        </gofix-form-input>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col md="12" class="text-right">
                        <gofix-button
                            label="SAVE"
                            :loading="isSave"
                            @click="$emit('save')"
                            :disabled="isSave"
                            noBorder>
                        </gofix-button>
                    </b-col>
                </b-row>
            </div>
        `
    }

    export default {
        name: "dropdown-locations",
        components: {
            LocationUpsertPanel
        },
        props: {
            value: {
                required: true,
                default: () => null
            },
            projectId: {
                type: Number,
                required: false,
                default: null
            },
            vLabel: {
                type: String,
                required: false,
                default: () => "name"
            },
            label: {
                type: String,
                required: false,
                default: () => "Location"
            },
            filterable: {
                type: Boolean,
                required: false,
                default: () => false
            },
            required: {
                type: Boolean,
                required: false,
                default: () => false
            },
            showIconRequired: {
                type: Boolean,
                required: false,
                default: () => false
            },
            multiple: {
                type: Boolean,
                required: false,
                default: () => false
            },
            appendToBody: {
                type: Boolean,
                required: false,
                default: () => true
            },
            disabled: {
                type: Boolean,
                default: false
            },
            reduce: {
                type: Function,
                default: option => {
                    if(option && option.equipment_id){
                        option.id = option.id + option.equipment_id
                    }
                    return option
                },
            },
            isUpsertLocation: {
                type: Boolean,
                default: false
            },
            isInModal: {
                type: Boolean,
                default: false
            },
        },

        data() {
            return {
                options: [],
                timer: null,
                isShowUpsertLocation: false,
                location: {
                    id: null,
                    postal_code: null,
                    lift_number: null,
                    name: null,
                    geo_location: {
                        latitude: null,
                        longtitude: null
                    }
                },
                oneMapLocations: [],
                isCreate: false,
                isSave: false,
                isSearching: false,
                timer: {
                    save: null
                }
            }
        },
        computed: {
            upsertModalTitle() {
                return this.isCreate ? 'Add Location' : 'Edit Location'
            }
        },
        watch: {
            projectId: function() {
                this.getOptions()
            },
            isUpsertLocation: function(val) {
                if(!val) {
                    this.resetLocation()
                    this.isShowUpsertLocation = false
                }
            },
            value: function(val) {
                if(val) {
                    this.location = {
                        id: this.value.id,
                        name: this.value.name,
                        lift_number: null,
                        postal_code: null,
                        geo_location: {
                            latitude: null,
                            longtitude: null
                        }
                    }
                    this.isCreate = false
                }
            }
        },
        created(){
            this.getOptions()
        },
        mounted() {
            this.tooltipEl = document.createElement('div')
            this.tooltipEl.className = 'location-option-tooltip-portal'
            this.tooltipEl.style.cssText = [
                'display:none',
                'position:fixed',
                'z-index:2147483647',
                'max-width:420px',
                'padding:6px 10px',
                'border-radius:4px',
                'background:rgba(0,0,0,0.85)',
                'color:#fff',
                'font-size:12px',
                'line-height:1.4',
                'pointer-events:none',
                'white-space:normal',
                'word-break:break-word',
                'transform:translateX(-50%)',
                'box-shadow:0 4px 12px rgba(0,0,0,0.25)'
            ].join(';')
            document.body.appendChild(this.tooltipEl)
        },
        beforeDestroy() {
            this.hideOptionTooltip()
            if (this.tooltipEl && this.tooltipEl.parentNode) {
                this.tooltipEl.parentNode.removeChild(this.tooltipEl)
            }
            this.tooltipEl = null
        },
        methods: {
            getOptionLabel(option) {
                if (!option) return ''
                let label = option.name ? String(option.name) : ''
                if (option.equipment_type) label += ` ${option.equipment_type}`
                if (option.equipment_lift_number) label += ` ${option.equipment_lift_number}`
                return label.trim()
            },
            getDropdownMenuZIndex(triggerEl) {
                let menu = null
                if (triggerEl) {
                    menu = triggerEl.closest
                        ? triggerEl.closest('.vs__dropdown-menu')
                        : null
                }
                if (!menu) {
                    menu = document.querySelector('body > .vs__dropdown-menu, .vs__dropdown-menu')
                }
                if (!menu) return 99999

                let z = parseInt(window.getComputedStyle(menu).zIndex, 10)
                if (isNaN(z)) z = 99999
                return z
            },
            showOptionTooltip(event, option) {
                const label = this.getOptionLabel(option)
                if (!label || !this.tooltipEl) return

                const triggerEl = event.currentTarget
                const rect = triggerEl.getBoundingClientRect()
                const tip = this.tooltipEl

                // Keep tooltip after dropdown menu in DOM + higher z-index
                // (.vs__dropdown-menu uses z-index: 99999 in _forms.scss)
                const menuZ = this.getDropdownMenuZIndex(triggerEl)
                tip.style.zIndex = String(menuZ + 100)
                document.body.appendChild(tip)

                tip.textContent = label
                tip.style.display = 'block'

                const tipWidth = tip.offsetWidth || 200
                const tipHeight = tip.offsetHeight || 32
                const gap = 8

                // Center horizontally above the hovered item
                let left = rect.left + rect.width / 2
                const halfWidth = tipWidth / 2
                if (left - halfWidth < 8) left = halfWidth + 8
                if (left + halfWidth > window.innerWidth - 8) {
                    left = window.innerWidth - halfWidth - 8
                }

                // Prefer above the item; flip below if not enough space
                let top = rect.top - tipHeight - gap
                if (top < 8) {
                    top = rect.bottom + gap
                }

                tip.style.left = left + 'px'
                tip.style.top = top + 'px'
            },
            hideOptionTooltip() {
                if (this.tooltipEl) {
                    this.tooltipEl.style.display = 'none'
                }
            },
            async getOptions(search = '') {
                if(!this.projectId) {
                    this.options = []
                    return
                }
                let tableRequest = {
                    filter: { 
                        project_id: this.projectId,
                        name: search 
                    }, 
                    page: 1, 
                    limit: 100
                }
                await this.$store.dispatch("SafetyAppController/getLocations", tableRequest)
                    .then(response => {
                        this.options = response.items;
                    })
                    .catch(err => {})
            },
            async searchOptions(search, loading) {
                loading(true)
                this.options = []
                if (this.timer) {
                    clearTimeout(this.timer);
                    this.timer = null;
                }
                this.timer = setTimeout(async () => {
                    try {
                        await this.getOptions(search);
                        await loading(false)
                    } catch(e) {
                        loading(false)
                    }
                }, 500);
            },
            handleInput (e) {
                this.hideOptionTooltip()
                this.$emit("input", e);
                this.$emit("change", e);
            },
            showUpsertLocation(type) {
                const isCreate = type === 'add'
                if (isCreate) {
                    this.resetLocation()
                } else if (this.value) {
                    this.location = {
                        id: this.value.id,
                        name: this.value.name,
                        lift_number: null,
                        postal_code: null,
                        geo_location: {
                            latitude: null,
                            longtitude: null
                        }
                    }
                }

                // Same mode (or first open) → toggle; switching mode → keep open
                if (!this.isShowUpsertLocation || isCreate === this.isCreate) {
                    this.isShowUpsertLocation = !this.isShowUpsertLocation
                }

                this.isCreate = isCreate
            },
            resetLocation() {
                this.location = {
                    id: null,
                    name: null,
                    lift_number: null,
                    postal_code: null,
                    geo_location: {
                        latitude: null,
                        longtitude: null
                    }
                }
            },
            handleSearchPostalCode() {
                if(this.isSearching) return
                this.isSearching = true
                let params = {
                    searchVal: this.location.postal_code
                }
                this.$store.dispatch("SafetyAppController/getOneMapLocations", params)
                    .then(data => {
                        if(data && data.hasOwnProperty('ADDRESS')) {
                            this.location.name = data.ADDRESS
                            this.location.geo_location.latitude = data.LATITUDE
                            this.location.geo_location.longtitude = data.LONGITUDE
                        } else {
                            this.location.name = null
                            this.location.geo_location.latitude = null
                            this.location.geo_location.longtitude = null
                        }
                    })
                    .catch(err => {
                        if(err.response && err.response.data && err.response.data.error && err.response.data.error.message) {
                            this.$alertNotification(err.response.data.error.message, "warning")
                        }
                    })
                    .finally(() => {
                        this.isSearching = false
                    })
            },
            saveLocation() {
                if (this.timer.save) {
                    clearTimeout(this.timer.save);
                    this.timer.save = null;
                }
                this.timer.save = setTimeout(() => {

                    const validate_fields = [
                        { key: 'name', name: 'Location Name'},
                    ]

                    if(this.isCreate) {
                        validate_fields.push({ key: 'lift_number', name: 'Lift Number'})
                    }

                    const is_validate = this.$validationData(validate_fields, this.location)
                    if(is_validate) return

                    if(this.isSave) return
                    this.isSave = true

                    let params = {
                        id: null,
                        data: null
                    }
                    
                    if(this.isCreate) {
                        params.data = {
                            contract_id: this.ClientContractID.id,
                            project_id: this.projectId,
                            name: `${this.location.name} Lift ${this.location.lift_number}`,
                            geo_location: {
                                latitude: this.location.geo_location.latitude ? parseFloat(this.location.geo_location.latitude) : null,
                                longtitude: this.location.geo_location.longtitude ? parseFloat(this.location.geo_location.longtitude) : null
                            },
                        }
                    } else {
                        params.id = this.location.id
                        params.data = {
                            name: this.location.name,
                            contract_id: this.ClientContractID.id,
                            project_id: this.projectId,
                        }
                    }

                    const wasCreate = this.isCreate

                    this.$store.dispatch(`SafetyAppController/saveSafetyLocation`, params)
                    .then(async response => {
                        await this.getOptions()

                        let selected = this.options.find(item => item.id === response.id || item.location_id === response.id)
                        if (!selected) {
                            selected = {
                                id: response.id,
                                location_id: response.id,
                                name: response.name || (wasCreate
                                    ? `${this.location.name} Lift ${this.location.lift_number}`
                                    : this.location.name),
                            }
                            this.options = [selected].concat(this.options)
                        }

                        this.handleInput(selected)
                        this.isShowUpsertLocation = false
                        this.resetLocation()
                        this.isCreate = false
                        this.$alertNotification(this.$notificationV2(wasCreate ? 'adding' : 'editing', 'location'));
                    })
                    .catch(err => {
                    }).finally(() => {
                        this.isSave = false
                    })
                }, 300);
            }
        }
    }
</script>

<style scoped>
.dropdown-locations-row {
    width: 100%;
    min-width: 0;
}

.property-dropdown-container.has-location-actions {
    position: relative;
    width: 100%;
    min-width: 0;
}

.property-dropdown-container.has-location-actions .property-dropdown {
    width: calc(100% - 72px);
    max-width: calc(100% - 72px);
}

.property-dropdown-container.no-location-actions .property-dropdown {
    width: 100%;
    max-width: 100%;
}

.location-actions {
    position: absolute;
    top: 10px;
    right: 0;
    display: flex;
    align-items: center;
    height: 38px;
    gap: 4px;
}

.location-action-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin: 0;
    flex: 0 0 32px;
}

.location-option-label {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
